import { apiTarefas } from '@modules/tarefas/api/apiTarefas'
import { apiSprints } from '@modules/tarefas/api/apiSprints'
import { diffTimeMinutes } from '@utils/Format'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import { buildTasksQueryFromState } from '@modules/tarefas/utils/filterQuery'
import { toApiDate } from '@modules/tarefas/utils/calendarWindow'

// Mensagem de fallback do erro de carga da lista (AC-1.7), usada quando a
// resposta não traz `message`. A copy que a tela renderiza é fixa no
// `task-list-view` (design/journey.md §2.13) — este texto é o que o store
// guarda para diagnóstico e para consumidores que leiam `tasksLoadError`.
const TASKS_LOAD_ERROR_FALLBACK_MESSAGE =
  'Não foi possível carregar as tarefas. Tente novamente.'

// Etiqueta de recurso enviada no sinal W8/F6 (perda de acesso). A view
// (`view-task-board`) mapeia para a copy do snack ("...esta lista..."). Evita
// string mágica no commit. `loadTasks` carrega as tarefas de uma LISTA (sprint).
const ACCESS_LOST_RESOURCE_LISTA = 'lista'

// Mensagem de fallback da Agenda — mesma ideia de TASKS_LOAD_ERROR_FALLBACK_MESSAGE,
// mas para o slice isolado `agendaError` (ver `loadAgendaTasks`).
const AGENDA_LOAD_ERROR_FALLBACK_MESSAGE =
  'Não foi possível carregar a agenda. Tente novamente.'

// Returns true when the cached timestamp is fresh (within TTL window).
// Mirrors the pattern used by `apiTarefas.carregarAssignees/carregarTags`:
// `diffTimeMinutes(ts, ttl)` returns truthy when stale (or null), so a
// fresh cache is the falsy case.
function _isFresh(ts) {
  return ts && !diffTimeMinutes(ts, MINUTES_TIMEOUT_REQUESTS)
}

// Defensive ASC sort by `sortOrder` (`Number.MAX_SAFE_INTEGER` as null fallback)
// with stringified `id` as tie-breaker. Used both by `selectFirstFolderIfNeeded`
// (initial pick) and `deleteFolder` (next-active pick after delete).
function _sortFoldersBySortOrder(folders) {
  if (!Array.isArray(folders)) return []
  return folders.slice().sort(function (a, b) {
    const orderA = a && a.sortOrder != null ? a.sortOrder : Number.MAX_SAFE_INTEGER
    const orderB = b && b.sortOrder != null ? b.sortOrder : Number.MAX_SAFE_INTEGER
    if (orderA !== orderB) return orderA - orderB
    const idA = a && a.id != null ? String(a.id) : ''
    const idB = b && b.id != null ? String(b.id) : ''
    if (idA < idB) return -1
    if (idA > idB) return 1
    return 0
  })
}

export async function loadFolders({ commit, dispatch }) {
  commit('SET_LOADING', true)
  // try/finally garante que o loading flag é resetado mesmo se o connection.js
  // mudar contrato e passar a lançar exceção (hoje retorna {} em erro).
  try {
    const r = await apiTarefas.getFolders()
    if (r && r.status === 200) {
      commit('SET_FOLDERS', r.value)
      // After folders arrive, auto-select the first one so the Board can
      // render Estado B (or Normal) instead of Estado A whenever there is
      // at least one folder. Idempotent: no-op when activeFolderId is set.
      dispatch('selectFirstFolderIfNeeded')
    }
    return r
  } finally {
    commit('SET_LOADING', false)
  }
}

// Dispatches `setActiveFolderId` for the first folder by `sortOrder ASC`.
// Pure dispatch chain — never touches the network.
export function selectFirstFolderIfNeeded({ state, dispatch }) {
  if (state.activeFolderId !== null && state.activeFolderId !== undefined) return
  if (!Array.isArray(state.folders) || state.folders.length === 0) return
  const first = _sortFoldersBySortOrder(state.folders)[0]
  if (first && first.id != null) {
    dispatch('setActiveFolderId', first.id)
  }
}

export async function loadTasks({ commit, state }, { sprintId, silent = false } = {}) {
  // `silent: true` troca os dados em-place sem flipar o SET_LOADING global.
  // O Board (`applyBoardFilters`) recarrega SEM silent — o skeleton só
  // aparece no cold-load (`loading && !hasFolders`); o reload é indicado pelo
  // `v-progress-linear` escopado no `task-list-view`.
  if (!silent) commit('SET_LOADING', true)
  // Montagem do payload (data + boardFilters + closed flag) vive no util
  // `filterQuery` — a action só orquestra fetch + commit. A flag de tarefa
  // fechada (includeClosed, AC-9) flui via buildTasksQueryFromState a partir
  // do state.
  const r = await apiTarefas.getTasks(buildTasksQueryFromState(state, sprintId))
  // Defensivo: status != 200 zera a lista para a view refletir o filtro em vez
  // de manter dados antigos (inclui o caso W8: 404 por perda de acesso). A
  // falha deixa de ser silenciosa (AC-1.7): o erro é comitado DEPOIS do
  // `SET_TASKS([])`, que zera `tasksLoadError`.
  if (r && r.status === 200) {
    commit('SET_TASKS', r.value || [])
  } else {
    commit('SET_TASKS', [])
    commit('SET_TASKS_LOAD_ERROR', {
      message: (r && r.message) || TASKS_LOAD_ERROR_FALLBACK_MESSAGE,
    })
    // W8/F6 — perda de acesso à LISTA selecionada. O backend normaliza
    // lista-vazia para 200 [] (usecase_task_management.get_tasks), então um 404
    // que chega aqui é perda de acesso real ao recurso selecionado, NÃO um
    // "sem tarefas". Sinaliza para a view orquestrar snack + reload da árvore +
    // limpar seleção (a snack vive na view — `$showSnack` não existe no store).
    // Outros erros (500, etc.) apenas zeram a lista (comportamento existente).
    if (r && r.status === 404) {
      commit('SIGNAL_ACCESS_LOST', { recurso: ACCESS_LOST_RESOURCE_LISTA })
    }
  }
  if (!silent) commit('SET_LOADING', false)
  return r
}

// Carrega as tarefas da janela visível da Agenda (semana/mês do calendário).
//
// ★ Slice ISOLADO de propósito (decisão de produto 2026-08-28): a Agenda
// busca a PRÓPRIA janela de datas via `startDate`/`endDate` recebidos do
// caller (`utils/calendarWindow.js#resolveCalendarWindow`) e NUNCA toca
// `state.dateFilter`/`state.tasks` — navegar de mês na Agenda não pode mudar
// o que Lista e Kanban mostram, nem a URL de filtros do board. Por isso esta
// action NUNCA comita `SET_DATE_FILTER` nem `SET_TASKS`, só os equivalentes
// do slice `agenda*`.
//
// O payload reusa `buildTasksQueryFromState` (mesmos filtros de servidor —
// responsável/cliente/tag/includeClosed — que o board já aplica) e
// SOBRESCREVE `startDate`/`endDate` com os da janela recebida, nunca com
// `state.dateFilter`.
//
// `key` é o token de race-guard (AC de descarte de resposta obsoleta):
// commitamos `SET_AGENDA_WINDOW` de forma síncrona ANTES do fetch, então uma
// segunda chamada desta action (clique rápido em `>`/`<`) já sobrescreve
// `state.agendaWindow.key` enquanto a primeira ainda está em voo. Quando a
// primeira resposta chega, comparamos `key` recebido com `state.agendaWindow`
// atual — se divergiu, a resposta é descartada em silêncio (sem comitar
// tasks nem erro), pois já não corresponde à janela que a tela quer mostrar.
export async function loadAgendaTasks({ commit, state }, { startDate, endDate, key } = {}) {
  commit('SET_AGENDA_WINDOW', { startDate: startDate, endDate: endDate, key: key })
  commit('SET_AGENDA_LOADING', true)
  try {
    // `toApiDate` converte a janela de ISO para DD/MM/YYYY — sem isso o
    // guard `_isCompleteDate` da camada de API descarta as duas datas em
    // silêncio e a Agenda busca a lista inteira. Ver o header do helper.
    const query = {
      ...buildTasksQueryFromState(state, state.activeSprintId),
      startDate: toApiDate(startDate),
      endDate: toApiDate(endDate),
    }
    const r = await apiTarefas.getTasks(query)

    const currentWindowKey = state.agendaWindow && state.agendaWindow.key
    if (currentWindowKey !== key) return r

    if (r && r.status === 200) {
      commit('SET_AGENDA_TASKS', r.value || [])
      commit('SET_AGENDA_ERROR', null)
    } else {
      // Mantém `agendaTasks` como estava — a Agenda continua mostrando a
      // última janela válida em vez de esvaziar o calendário numa falha
      // transiente (diferente de `loadTasks`, que zera `state.tasks`).
      commit('SET_AGENDA_ERROR', {
        message: (r && r.message) || AGENDA_LOAD_ERROR_FALLBACK_MESSAGE,
      })
    }
    return r
  } finally {
    // Só desliga o loading quando esta chamada AINDA é a janela corrente —
    // numa resposta OBSOLETA (janela já trocou durante o fetch, ex.: dois
    // cliques rápidos em '>'), a resposta antiga NÃO pode apagar o skeleton
    // enquanto a resposta da janela certa ainda está em voo (violaria
    // US-4 — "WHILE a busca estiver em andamento THE system SHALL exibir
    // loading"). O `finally` roda nos dois branches do try (sucesso e erro
    // da janela corrente), então o loading nunca fica preso.
    if ((state.agendaWindow && state.agendaWindow.key) === key) {
      commit('SET_AGENDA_LOADING', false)
    }
  }
}

// Closed-task visibility setter (AC-9). Commits the "Mostrar fechadas" flag
// then reloads tasks so the Board list/kanban reflect the new universe
// immediately. The current sprint (state.activeSprintId) is preserved.
export function setIncludeClosed({ commit, dispatch, state }, value) {
  commit('SET_INCLUDE_CLOSED', value)
  return dispatch('loadTasks', { sprintId: state.activeSprintId })
}

// Client-side search — commit puro, sem fetch. O getter
// `currentSprintTasks` aplica o filtro sobre o que já está no Vuex, então
// digitar é instantâneo e não invalida o cache do board.
export function setSearchQuery({ commit }, value) {
  commit('SET_SEARCH_QUERY', value)
}

// Client-side status filter (AC-13) — commit puro, sem fetch. O getter
// `currentSprintTasks` aplica o filtro sobre as tasks já no Vuex.
export function setStatusFilter({ commit }, value) {
  commit('SET_STATUS_FILTER', value)
}

// Helper: extract overview filter dimensions from current state. Centralized
// so every loadOverview* action stays in sync if the filter shape evolves.
function _overviewFilterArgs(state) {
  const f = state.overviewFilters || {}
  return {
    startDate: f.startDate || null,
    endDate: f.endDate || null,
    responsavelIds: Array.isArray(f.responsavelIds) ? f.responsavelIds : [],
    clientIds: Array.isArray(f.clientIds) ? f.clientIds : [],
  }
}

export async function loadOverviewStats({ commit, state }) {
  let r = await apiTarefas.getOverviewStats(_overviewFilterArgs(state))
  if (r && r.status === 200) {
    commit('SET_OVERVIEW_STATS', r.value)
  }
  return r
}

export async function createTask({ commit }, { task }) {
  let r = await apiTarefas.createTask({ task: task })
  if (r && r.status === 200) {
    commit('ADD_TASK', r.value)
  }
  return r
}

export async function updateTask({ commit }, { id, data }) {
  let r = await apiTarefas.updateTask({ id: id, data: data })
  if (r && r.status === 200) {
    commit('UPDATE_TASK', r.value)
  }
  return r
}

// Pure state action (no network): moves a task to `statusId` locally so the
// Kanban card / List row jumps to the target column the moment it is dropped,
// instead of only when the PUT resolves. The caller keeps the previous values
// and replays this action to roll back when the request fails.
export function patchTaskStatus({ commit }, { id, statusId, statusCategory }) {
  commit('PATCH_TASK_STATUS', {
    id: id,
    statusId: statusId,
    statusCategory: statusCategory,
  })
}

// Setter PURO — so comita. Quem chama e `apiTarefas.deleteTasks`, depois de o
// servidor confirmar quais ids foram de fato removidos; os componentes chamam a
// camada de API direto (mesmo padrao de `carregarQuickViewTasks`), nunca esta
// action. Nao ha mais action `deleteTask`/`deleteTasks` fazendo fetch: a regra
// do modulo e componente -> classe de API -> store, com a action so no fim.
//
// `REMOVE_TASKS` remove os ids em QUALQUER nivel da arvore (raiz ou subtarefa),
// nao so entre as raizes.
export function removeTasksFromStore({ commit }, { taskIds } = {}) {
  commit('REMOVE_TASKS', taskIds || [])
}

// `parentId` (opcional) cria a pasta como SUBPASTA da pasta informada. A
// action só repassa — profundidade, existência da mãe e permissão são regra
// de negócio validada no backend.
export async function createFolder({ commit, dispatch }, { name, parentId = null } = {}) {
  const r = await apiTarefas.createFolder({ name: name, parentId: parentId })
  if (r && r.status === 200) {
    commit('ADD_FOLDER', r.value)
    // Auto-seleciona a pasta recém-criada. Sem isso, a Board cai em Normal
    // (porque activeFolderId continua null) e mostra a toolbar com
    // "Adicionar Tarefa" mesmo sem nenhuma sprint criada — confunde o
    // usuário e quebra a transição Estado A → Estado B. Match com a
    // expectativa de "criei agora, me leva pra ela" (Notion/ClickUp).
    if (r.value && r.value.id != null) {
      // Sai do modo visão rápida ao criar (S1): se o usuário criou a pasta
      // estando numa visão rápida vazia, a Board ficaria presa na lista
      // cross-pasta e a pasta nova (+ Estado B) nunca apareceria.
      commit('SET_ACTIVE_QUICK_VIEW', null)
      dispatch('setActiveFolderId', r.value.id)
    }
  }
  return r
}

export async function updateFolder({ commit }, { id, data }) {
  let r = await apiTarefas.updateFolder({ id: id, data: data })
  if (r && r.status === 200) {
    commit('UPDATE_FOLDER', r.value)
  }
  return r
}

export async function deleteFolder({ commit, state, dispatch }, { id }) {
  // Snapshot taken BEFORE the mutation: REMOVE_FOLDER does not touch
  // activeFolderId, so we need to detect "active folder was deleted" before
  // the list shrinks (defensive — also keeps the logic readable).
  const wasActive = state.activeFolderId === id
  let r = await apiTarefas.deleteFolder({ id: id })
  if (r && r.status === 200) {
    commit('REMOVE_FOLDER', id)
    if (wasActive) {
      // Pick the next folder by sortOrder ASC (id as tie-breaker) or fall
      // back to null when the user just deleted the last one (Estado A).
      // Always clear activeSprintId so we don't leak tasks from the deleted
      // folder into the next view.
      const remaining = _sortFoldersBySortOrder(state.folders)
      const next = remaining.length > 0 ? remaining[0] : null
      dispatch('setActiveFolderId', next ? next.id : null)
      dispatch('setActiveSprintId', null)
    }
  }
  return r
}

export async function createSprint({ commit, dispatch }, { folderId, sprint }) {
  const r = await apiSprints.createSprint({ folderId: folderId, sprint: sprint })
  if (r && r.status === 200) {
    commit('ADD_SPRINT', { folderId: folderId, sprint: r.value })
    // Seleciona a lista recém-criada via `selectSprint`: além de marcar a
    // sprint ativa, ele SAI do modo visão rápida (S1), limpa o filtro de data
    // e carrega as tarefas (lista nova = vazia, já com "+ Adicionar tarefa").
    // Usar `setActiveSprintId` direto deixava a Board presa numa visão rápida
    // vazia ao criar a lista de lá, e não disparava o loadTasks. O autoSelect
    // do sidebar só pega sprints 'active'; uma lista nova ('planning') não
    // seria pega — por isso a seleção explícita.
    if (r.value && r.value.id != null) {
      dispatch('selectSprint', { folderId: folderId, sprintId: r.value.id })
    }
  }
  return r
}

export async function updateSprint({ commit }, { folderId, sprintId, data }) {
  let r = await apiSprints.updateSprint({ sprintId: sprintId, data: data })
  if (r && r.status === 200) {
    commit('UPDATE_SPRINT', { folderId: folderId, sprint: r.value })
  }
  return r
}

export async function deleteSprint({ commit }, { folderId, sprintId }) {
  let r = await apiSprints.deleteSprint({ sprintId: sprintId })
  if (r && r.status === 200) {
    commit('REMOVE_SPRINT', { folderId: folderId, sprintId: sprintId })
  }
  return r
}

export function setActiveFolderId({ commit, dispatch }, folderId) {
  commit('SET_ACTIVE_FOLDER_ID', folderId)
  // Warm the folder's custom-status slice (customizar-status-tarefas) so the
  // board columns / chip / filter render the right set as soon as the folder
  // becomes active. Fire-and-forget; TTL-cached so repeated activations are
  // cheap. Guard the null/clear case (deleted last folder → Estado A).
  if (folderId != null) {
    dispatch('loadFolderStatuses', { folderId: folderId })
  }
}

export function setActiveSprintId({ commit }, sprintId) {
  commit('SET_ACTIVE_SPRINT_ID', sprintId)
}

export function setDateFilter({ commit }, { startDate, endDate } = {}) {
  commit('SET_DATE_FILTER', { startDate: startDate, endDate: endDate })
}

// --- Multi-column sort chain -------------------------------------------
// The List headers + Sort control dispatch these (never commit directly).

// Replaces the whole chain with a normalized copy. `payload` may be
// `{ chain }` or a bare array — both accepted for caller convenience.
export function setSortChain({ commit }, payload) {
  const chain =
    payload && Object.hasOwn(payload, 'chain') ? payload.chain : payload
  commit('SET_SORT_CHAIN', Array.isArray(chain) ? chain : [])
}

// Cycles a column asc → desc → off. `additive` (shift+click / "+ ordenar")
// appends to the chain; otherwise it replaces the chain with this column.
export function cycleSortColumn({ commit }, { field, additive = false } = {}) {
  commit('CYCLE_SORT_COLUMN', { field: field, additive: additive })
}

export function clearSortChain({ commit }) {
  commit('CLEAR_SORT_CHAIN')
}

// Back-compat: legacy `{ field, direction }` partial. Delegates to SET_SORT
// (writes a 1-element chain). Kept so older callers keep working.
export function setSort({ commit }, payload) {
  commit('SET_SORT', payload || {})
}

// Orquestra a troca de sprint: ativa pasta/sprint, reseta o filtro de data
// (sempre começa vazio — sem sugerir as datas da lista) e dispara loadTasks
// numa ordem que evita o duplo-fetch (set sprint → clear filter → fetch).
export function selectSprint(
  { commit, dispatch },
  { folderId, sprintId, preserveDateFilter = false, skipLoadTasks = false } = {}
) {
  // Selecionar uma sprint sai do modo visão rápida (estado mutuamente
  // exclusivo — S1): ou sprint ativa, ou visão rápida ativa, nunca ambos.
  commit('SET_ACTIVE_QUICK_VIEW', null)
  if (folderId !== undefined) {
    commit('SET_ACTIVE_FOLDER_ID', folderId)
    // Warm the folder's custom-status slice on sprint selection too (the
    // board renders columns from it). TTL-cached, fire-and-forget.
    if (folderId != null) dispatch('loadFolderStatuses', { folderId: folderId })
  }
  commit('SET_ACTIVE_SPRINT_ID', sprintId)
  // Troca de sprint iniciada pelo usuário zera o filtro de data (sempre começa
  // vazio). `preserveDateFilter` é usado APENAS pela hidratação por URL no
  // reload — restaurar a lista NÃO pode apagar a data que veio na URL.
  if (!preserveDateFilter) {
    commit('SET_DATE_FILTER', { startDate: null, endDate: null })
  }
  // `skipLoadTasks: true` ativa só o CONTEXTO da sprint (pasta/sprint/statuses
  // p/ o agrupamento nativo) sem buscar TODAS as tasks do sprint. Usado pela aba
  // operação-AAWZ, que supre suas próprias tasks via `setTasks` (recorte só das
  // solicitações) — sem isso, o loadTasks reescreveria state.tasks com as avulsas.
  if (skipLoadTasks) return
  return dispatch('loadTasks', { sprintId: sprintId })
}

// --- Visões rápidas (cross-pasta) --------------------------------------
// `selectQuickView` entra no modo visão rápida: deseleciona pasta/sprint,
// limpa o filtro de data herdado da sprint e marca a visão ativa. É uma action
// PURA DE ESTADO — não faz chamada de rede. A busca da lista cross-pasta e dos
// contadores vive na camada de API (`apiTarefas.carregarQuickViewTasks` /
// `carregarQuickViewCounts`), chamada pelos componentes após selecionar a
// visão. Os setters abaixo (`setLoading`/`setTasks`/`setQuickViewCounts`) são
// os pontos por onde a camada de API popula o store.

export function selectQuickView({ commit }, { viewKey } = {}) {
  commit('SET_ACTIVE_FOLDER_ID', null)
  commit('SET_ACTIVE_SPRINT_ID', null)
  commit('SET_DATE_FILTER', { startDate: null, endDate: null })
  // Conjunto novo = paginação nova (spec-002 F4). Sem isto a visão nova herda o
  // `total` da anterior e `hasMoreQuickViewPages` mente até a resposta chegar —
  // o botão "Carregar mais" apareceria (ou desapareceria) errado no clique.
  commit('SET_QUICK_VIEW_PAGINATION', { page: 1, pageSize: 0, total: 0 })
  commit('SET_ACTIVE_QUICK_VIEW', viewKey)
}

export function setLoading({ commit }, value) {
  commit('SET_LOADING', !!value)
}

export function setTasks({ commit }, tasks) {
  commit('SET_TASKS', tasks || [])
}

export function setQuickViewCounts({ commit }, counts) {
  commit('SET_QUICK_VIEW_COUNTS', counts || {})
}

// Acrescenta uma página ao fim da lista (spec-002 F4). Setter puro, como
// `setTasks` — quem faz a rede é a camada de API (`carregarQuickViewTasks`).
export function appendTasks({ commit }, tasks) {
  commit('APPEND_TASKS', tasks || [])
}

export function setQuickViewPagination({ commit }, pagination) {
  commit('SET_QUICK_VIEW_PAGINATION', pagination)
}

// Pura: só aplica o comentário criado ao estado. A chamada de API
// (apiTarefas.addComment) é feita pelo componente; o store não orquestra rede.
export function applyComment({ commit }, { taskId, comment }) {
  commit('ADD_COMMENT', { taskId: taskId, comment: comment })
}

export async function updateComment({ commit }, { taskId, commentId, text }) {
  let r = await apiTarefas.updateComment({ commentId: commentId, text: text })
  if (r && r.status === 200) {
    commit('UPDATE_COMMENT', { taskId: taskId, comment: r.value })
  }
  return r
}

export async function deleteComment({ commit }, { taskId, commentId }) {
  let r = await apiTarefas.deleteComment({ commentId: commentId })
  if (r && r.status === 200) {
    commit('REMOVE_COMMENT', { taskId: taskId, commentId: commentId })
  }
  return r
}

// Pura: só aplica ao estado a remoção de um anexo de comentário (a chamada de
// API apiTarefas é feita pelo componente; o store não orquestra rede).
export function removeCommentAttachment(
  { commit },
  { taskId, commentId, attachmentId }
) {
  commit('REMOVE_COMMENT_ATTACHMENT', {
    taskId: taskId,
    commentId: commentId,
    attachmentId: attachmentId,
  })
}

// Anexos de TAREFA: action pura — só aplica ao estado. A chamada de API
// (apiTarefas) é feita pelo componente; o store não orquestra rede.
export function applyTaskAttachments({ commit }, { taskId, attachments }) {
  commit('ADD_TASK_ATTACHMENTS', {
    taskId: taskId,
    attachments: attachments || [],
  })
}

export function removeTaskAttachment({ commit }, { taskId, attachmentId }) {
  commit('REMOVE_TASK_ATTACHMENT', {
    taskId: taskId,
    attachmentId: attachmentId,
  })
}

export async function reorderFolders({ commit }, { orderedIds }) {
  let r = await apiTarefas.reorderFolders({ orderedIds: orderedIds })
  if (r && r.status === 200) {
    commit('REORDER_FOLDERS', orderedIds)
  }
  return r
}

export async function reorderSprints({ commit }, { folderId, orderedIds }) {
  let r = await apiSprints.reorderSprints({ folderId: folderId, orderedIds: orderedIds })
  if (r && r.status === 200) {
    commit('REORDER_SPRINTS', { folderId: folderId, orderedIds: orderedIds })
  }
  return r
}

export async function reorderTasks({ commit }, { sprintId, status, orderedIds }) {
  let r = await apiTarefas.reorderTasks({ sprintId: sprintId, status: status, orderedIds: orderedIds })
  if (r && r.status === 200) {
    commit('REORDER_TASKS', { sprintId: sprintId, status: status, orderedIds: orderedIds })
  }
  return r
}

// NOTE: "mover tarefas entre listas" não tem action Vuex própria. Seguindo a
// convenção do módulo (igual a `carregarQuickViewTasks`), a chamada de rede +
// o refresh-after-mutation vivem na camada de API (`apiTarefas.moveTasks`) e os
// componentes a chamam direto — actions não devem chamar a API.

/**
 * Sync action to seed `state.tags` (and `tagsLastUpdate`). Called by
 * `apiTarefas.carregarTags(...)` after a successful network response.
 * Components must always go through this action — never `commit` directly.
 */
export function setTags({ commit }, payload) {
  commit('SET_TAGS', payload || [])
}

export function createTag(_context, { name, color, active = true } = {}) {
  return apiTarefas.createTag({ name: name, color: color, active: active })
}

// --- Per-folder custom statuses (customizar-status-tarefas) -------------
// Each action dispatches the api method then commits the matching mutation.
// Components must always go through these actions — never commit directly.

/**
 * Sync setter used by `apiTarefas.carregarStatuses` after a 200 response.
 */
export function setFolderStatuses({ commit }, { folderId, statuses } = {}) {
  commit('SET_FOLDER_STATUSES', { folderId: folderId, statuses: statuses || [] })
}

export function setFolderStatusesLoading({ commit }, { folderId, value } = {}) {
  commit('SET_FOLDER_STATUSES_LOADING', { folderId: folderId, value: value })
}

/**
 * Loads a folder's statuses (TTL-cached via `carregarStatuses`). The cache
 * helper in the API layer owns the request AND the `SET_FOLDER_STATUSES`
 * write — this action is a dispatchable forwarder so other actions/components
 * can trigger a (force) load without re-importing the API layer.
 */
export function loadFolderStatuses(_context, { folderId, forceUpdate = false, includeInactive = false } = {}) {
  return apiTarefas.carregarStatuses({
    folderId: folderId,
    forceUpdate: forceUpdate,
    includeInactive: includeInactive,
  })
}

// Pure commit setters — the write request + cache write live in the API layer
// (`apiTarefas.criarStatus/atualizarStatus/removerStatus/reordenarStatuses`,
// which dispatch these). Actions never fetch — each layer owns its responsibility.

export function addFolderStatusToStore({ commit }, { folderId, status } = {}) {
  commit('ADD_FOLDER_STATUS', { folderId: folderId, status: status })
}

export function updateFolderStatusInStore({ commit }, { folderId, status } = {}) {
  commit('UPDATE_FOLDER_STATUS', { folderId: folderId, status: status })
}

export function removeFolderStatusFromStore({ commit }, { folderId, statusId } = {}) {
  commit('REMOVE_FOLDER_STATUS', { folderId: folderId, statusId: statusId })
}

export function setFolderStatusesOrder({ commit }, { folderId, orderedIds } = {}) {
  commit('SET_FOLDER_STATUSES_ORDER', { folderId: folderId, orderedIds: orderedIds })
}

/**
 * Attaches a tag to a task (junction `pm_task_tag`).
 * On 200, also commits `ADD_TAG_TO_TASK` so `state.tasks[i].tags` stays in
 * sync without a full board reload (optimistic ergonomics live in the picker).
 */
export async function addTagToTask({ commit }, { taskId, tag, tagId } = {}) {
  // Accept both `tag` (full object) and bare `tagId` for back-compat.
  let resolvedTagId = tag && tag.id ? tag.id : tagId
  let r = await apiTarefas.addTagToTask({ taskId: taskId, tagId: resolvedTagId })
  if (r && r.status === 200 && tag) {
    commit('ADD_TAG_TO_TASK', { taskId: taskId, tag: tag })
  }
  return r
}

export async function removeTagFromTask({ commit }, { taskId, tagId } = {}) {
  let r = await apiTarefas.removeTagFromTask({ taskId: taskId, tagId: tagId })
  if (r && r.status === 200) {
    commit('REMOVE_TAG_FROM_TASK', { taskId: taskId, tagId: tagId })
  }
  return r
}

export async function addAssignee(_context, { taskId, profissionalId }) {
  let r = await apiTarefas.addAssignee({ taskId: taskId, profissionalId: profissionalId })
  return r
}

export async function removeAssignee(_context, { taskId, profissionalId }) {
  let r = await apiTarefas.removeAssignee({ taskId: taskId, profissionalId: profissionalId })
  return r
}


export async function loadChecklists(_context, { taskId }) {
  let r = await apiTarefas.getChecklists({ taskId: taskId })
  return r
}

export async function createChecklist(_context, { taskId, name }) {
  let r = await apiTarefas.createChecklist({ taskId: taskId, name: name })
  return r
}

export async function createChecklistItem(_context, { checklistId, description }) {
  let r = await apiTarefas.createChecklistItem({ checklistId: checklistId, description: description })
  return r
}

export async function updateChecklistItem(_context, { itemId, data }) {
  let r = await apiTarefas.updateChecklistItem({ itemId: itemId, data: data })
  return r
}

export async function deleteChecklistItem(_context, { itemId }) {
  let r = await apiTarefas.deleteChecklistItem({ itemId: itemId })
  return r
}

export function setAssignees({ commit }, payload) {
  commit('SET_ASSIGNEES', payload)
  commit('SET_ASSIGNEES_LAST_UPDATE', Date.now())
}

export function setAssigneesLastUpdate({ commit }, ts) {
  commit('SET_ASSIGNEES_LAST_UPDATE', ts)
}

export function setAssigneesLoading({ commit }, val) {
  commit('SET_ASSIGNEES_LOADING', val)
}

// Legacy delegator preserved for backwards compatibility (DoD-7).
// Stays async to match the previous public signature so any code still
// calling `store.dispatch('tarefas/loadAssignees')` keeps receiving a
// promise. The single delegating call is intentional.
// eslint-disable-next-line require-await
export async function loadAssignees() {
  return apiTarefas.carregarAssignees()
}

export async function loadCustomFields({ commit }) {
  let r = await apiTarefas.getCustomFields()
  if (r && r.status === 200) {
    commit('SET_CUSTOM_FIELDS', r.value || [])
  }
  return r
}

export async function createCustomField({ commit }, { name, type, options, displayOrder }) {
  let r = await apiTarefas.createCustomField({ name: name, type: type, options: options, displayOrder: displayOrder })
  if (r && r.status === 200) {
    commit('UPSERT_CUSTOM_FIELD', r.value)
  }
  return r
}

export async function updateCustomField({ commit }, { id, data }) {
  let r = await apiTarefas.updateCustomField({ id: id, data: data })
  if (r && r.status === 200) {
    commit('UPSERT_CUSTOM_FIELD', r.value)
  }
  return r
}

export async function deleteCustomField({ commit }, { id }) {
  let r = await apiTarefas.deleteCustomField({ id: id })
  if (r && r.status === 200) {
    commit('REMOVE_CUSTOM_FIELD', id)
  }
  return r
}

export async function setTaskCustomFieldValue(_context, { taskId, fieldId, value }) {
  let r = await apiTarefas.setTaskCustomFieldValue({ taskId: taskId, fieldId: fieldId, value: value })
  return r
}

// --- List column preference (pm_user_list_preference) -------------------
// Preferência PESSOAL de colunas da List view, escopada por (usuário,
// sprint). `columns: null` no state = sem preferência → o getter
// `visibleListColumns` aplica o default do registry.

/**
 * Setter PURO do slice de preferência (commit-only). É o ponto único pelo qual
 * a camada de API (`apiTarefas.carregarListPreference`/`persistListPreference`/
 * `restaurarListPreference`) popula o estado — a action NUNCA faz rede, seguindo
 * o padrão do módulo (mesma decisão de `setTasks`/`setQuickViewCounts`). Aceita
 * payload parcial (a mutation `SET_LIST_PREFERENCE` coage defensivamente).
 */
export function setListPreference({ commit }, payload) {
  commit('SET_LIST_PREFERENCE', payload || {})
}

/**
 * Aplica um DRAFT local de colunas (UI-first, sem rede). Usado pelo menu de
 * colunas para refletir toggles/reordenação imediatamente enquanto o PUT é
 * debounced — o flush chama `saveListPreference` depois. Também serve para
 * limpar o slice quando a sprint ativa some (`sprintId: null`), garantindo
 * que `loading` nunca fica preso em true.
 */
export function applyListPreferenceLocal({ commit }, { sprintId, columns } = {}) {
  commit('SET_LIST_PREFERENCE', {
    sprintId: sprintId == null ? null : sprintId,
    columns: Array.isArray(columns) ? columns : null,
    loading: false,
  })
}

// Chaves fixas (sempre re-adicionadas pelo getter `visibleListColumns` como
// primeira/última, fora do controle de visibilidade). O backend
// (`_validate_columns_structure`) REJEITA essas chaves como string crua — elas
// só são válidas em `columns` como dict só-width `{key, width}`. Invariante:
// uma chave fixa NUNCA é serializada sem `width` (é OMITIDA do array; o getter
// a re-adiciona com o `cols` default). Ver REQUIREMENTS §9.2.
const FIXED_KEYS = new Set(['title', 'actions'])

function _isFixedKey(key) {
  return FIXED_KEYS.has(key)
}

// Normaliza um item de `columns` (string-v1 OU objeto {key, width}) para
// `{ key, width }` com `width` numérico-ou-undefined. Item inválido → null.
// Tolerante (sem optional chaining — Babel).
function _normalizeColumnItem(item) {
  if (typeof item === 'string') return { key: item, width: undefined }
  if (item && typeof item === 'object' && typeof item.key === 'string') {
    const width = typeof item.width === 'number' ? item.width : undefined
    return { key: item.key, width: width }
  }
  return null
}

// Reconstrói o array `columns` definindo/removendo o `width` da entrada `key`,
// PRESERVANDO ordem e visibilidade das demais. `width == null` remove a largura
// custom (entrada vira string-v1 — sem inventar width, AC-W12/W15). Quando a
// `key` não está presente (ex.: coluna fixa title/actions ou coluna default sem
// preferência salva ainda), ela é incluída a partir de `fallbackKeys` (chaves
// visíveis atuais, na ordem do getter) para não perder a configuração default.
// Serializa uma entrada normalizada com o width-alvo aplicado: a coluna `key`
// recebe o novo width; as demais preservam a forma. Chave fixa sem width →
// `_denormalize` retorna null (entrada OMITIDA; o getter re-adiciona title/actions).
function _rebuildEntry(norm, key, width) {
  const target = norm.key === key ? { key: norm.key, width: width == null ? undefined : width } : norm
  return _denormalize(target)
}

function _rebuildColumnsWithWidth(rawColumns, fallbackKeys, key, width) {
  const base = Array.isArray(rawColumns) && rawColumns.length ? rawColumns : fallbackKeys.slice()
  const out = []
  let touched = false
  for (const item of base) {
    const norm = _normalizeColumnItem(item)
    if (!norm) continue
    if (norm.key === key) touched = true
    const serialized = _rebuildEntry(norm, key, width)
    if (serialized != null) out.push(serialized)
  }
  // Chave ainda não presente no array (ex.: largura de fixa title/actions, ou
  // default-visível sem preferência) → adiciona uma entrada só-width.
  if (!touched && width != null) out.push({ key: key, width: width })
  return out
}

// Reconverte `{ key, width }` para a forma persistida mínima: string quando
// não há width, objeto quando há (não inventa width — AC-W15). Chave fixa
// (title/actions) sem width é INVÁLIDA como string crua (backend rejeita) →
// retorna null para o caller OMITIR a entrada (§9.2; o getter re-adiciona a
// fixa com o cols default).
function _denormalize(norm) {
  if (norm.width == null) {
    return _isFixedKey(norm.key) ? null : norm.key
  }
  return { key: norm.key, width: norm.width }
}

/**
 * Define (ou remove, com `width == null`) a largura de UMA coluna, reconstruindo
 * o array `columns` da preferência da sprint preservando ordem/visibilidade das
 * demais, e aplicando OTIMISTICAMENTE via `applyListPreferenceLocal`. Retorna o
 * array `columns` reconstruído para o componente agendar o PUT debounced
 * (mesmo pipeline do menu). NÃO faz rede — o flush é responsabilidade do caller.
 *
 * `fallbackKeys` (chaves visíveis atuais, na ordem do getter) garante que, sem
 * preferência salva ainda (default), a reconstrução preserve as colunas default
 * visíveis em vez de colapsar para uma lista vazia.
 */
export function setColumnWidth(
  { dispatch, state },
  { sprintId, key, width, fallbackKeys } = {}
) {
  if (!sprintId || typeof key !== 'string' || !key) return null
  const pref = state.listPreference || {}
  const rawColumns =
    pref.sprintId === sprintId && Array.isArray(pref.columns) ? pref.columns : null
  const keys = Array.isArray(fallbackKeys) ? fallbackKeys : []
  const columns = _rebuildColumnsWithWidth(rawColumns, keys, key, width == null ? null : width)
  dispatch('applyListPreferenceLocal', { sprintId: sprintId, columns: columns })
  return columns
}

// `loadListPreference`/`saveListPreference`/`resetListPreference` foram movidos
// para a camada de API (`apiTarefas.carregarListPreference`/`persistListPreference`/
// `restaurarListPreference`) — a action Vuex NUNCA faz fetch (review PR #1268,
// mesma decisão de `carregarQuickView*`/`moveTasks`). A persistência do slice
// passa pelo setter puro `setListPreference` acima.

export async function loadCurrentTaskDetail({ commit }, { id }) {
  let r = await apiTarefas.getTaskById({ id: id })
  if (r && r.status === 200) {
    // Only feed the modal's own detail slot. Do NOT mutate `state.tasks`
    // here: the detail payload has a different shape than list rows (its
    // `subtarefas` carry the nested tree, and subtasks are not at the list
    // root), so splicing it into `state.tasks` corrupts the list — opening a
    // task and closing the modal made its row disappear from the listing.
    // The list refreshes via `loadTasks` only after a real mutation
    // (see task-detail-modal `onTaskUpdated`).
    commit('SET_CURRENT_TASK_DETAIL', r.value)
  } else {
    commit('SET_CURRENT_TASK_DETAIL', null)
  }
  return r
}

// --- Overview actions (TTL-cached, 5-min by MINUTES_TIMEOUT_REQUESTS) ---
// Each action: skip network when the slot is fresh (unless `forceUpdate`),
// flips the loading flag, fetches via `apiTarefas`, commits the payload on
// success (preserving last-good cache on failure), and returns the raw
// response so callers can chain.

export async function loadOverviewOverdueTasks(
  { commit, state },
  { forceUpdate = false, limit = 10, silent = false } = {}
) {
  if (!forceUpdate && _isFresh(state.overview.lastUpdate.overdueTasks)) return null
  if (!silent) commit('SET_OVERVIEW_LOADING', { slot: 'overdueTasks', value: true })
  const r = await apiTarefas.getOverviewOverdueTasks({
    limit: limit, ..._overviewFilterArgs(state),
  })
  if (r && r.status === 200) {
    commit('SET_OVERVIEW_OVERDUE_TASKS', r.value || [])
  }
  if (!silent) commit('SET_OVERVIEW_LOADING', { slot: 'overdueTasks', value: false })
  return r
}

export async function loadOverviewWeeklyDue(
  { commit, state },
  { forceUpdate = false, limit = 10 } = {}
) {
  if (!forceUpdate && _isFresh(state.overview.lastUpdate.weeklyDue)) return null
  commit('SET_OVERVIEW_LOADING', { slot: 'weeklyDue', value: true })
  const r = await apiTarefas.getOverviewWeeklyDue({ limit: limit })
  if (r && r.status === 200) {
    commit('SET_OVERVIEW_WEEKLY_DUE', r.value || [])
  }
  commit('SET_OVERVIEW_LOADING', { slot: 'weeklyDue', value: false })
  return r
}

export async function loadOverviewWeeklyDone(
  { commit, state },
  { forceUpdate = false, limit = 10 } = {}
) {
  if (!forceUpdate && _isFresh(state.overview.lastUpdate.weeklyDone)) return null
  commit('SET_OVERVIEW_LOADING', { slot: 'weeklyDone', value: true })
  const r = await apiTarefas.getOverviewWeeklyDone({ limit: limit })
  if (r && r.status === 200) {
    commit('SET_OVERVIEW_WEEKLY_DONE', r.value || [])
  }
  commit('SET_OVERVIEW_LOADING', { slot: 'weeklyDone', value: false })
  return r
}

export async function loadOverviewMemberDistribution(
  { commit, state },
  { forceUpdate = false, limit = 10, silent = false } = {}
) {
  if (!forceUpdate && _isFresh(state.overview.lastUpdate.memberDistribution)) return null
  if (!silent) commit('SET_OVERVIEW_LOADING', { slot: 'memberDistribution', value: true })
  const r = await apiTarefas.getOverviewMemberDistribution({
    limit: limit, ..._overviewFilterArgs(state),
  })
  if (r && r.status === 200) {
    commit('SET_OVERVIEW_MEMBER_DISTRIBUTION', r.value || { members: [], hasOverdue: false })
  }
  if (!silent) commit('SET_OVERVIEW_LOADING', { slot: 'memberDistribution', value: false })
  return r
}

export async function loadOverviewClientDistribution(
  { commit, state },
  { forceUpdate = false, limit = 10, silent = false } = {}
) {
  if (!forceUpdate && _isFresh(state.overview.lastUpdate.clientDistribution)) return null
  if (!silent) commit('SET_OVERVIEW_LOADING', { slot: 'clientDistribution', value: true })
  const r = await apiTarefas.getOverviewClientDistribution({
    limit: limit, ..._overviewFilterArgs(state),
  })
  if (r && r.status === 200) {
    commit('SET_OVERVIEW_CLIENT_DISTRIBUTION', r.value || { members: [], hasOverdue: false })
  }
  if (!silent) commit('SET_OVERVIEW_LOADING', { slot: 'clientDistribution', value: false })
  return r
}

export async function loadOverviewChartMonthly(
  { commit, state },
  { forceUpdate = false, months = 12, silent = false } = {}
) {
  if (!forceUpdate && _isFresh(state.overview.lastUpdate.chartMonthly)) return null
  if (!silent) commit('SET_OVERVIEW_LOADING', { slot: 'chartMonthly', value: true })
  const r = await apiTarefas.getOverviewChartMonthly({
    months: months, ..._overviewFilterArgs(state),
  })
  if (r && r.status === 200) {
    commit('SET_OVERVIEW_CHART_MONTHLY', r.value || { categories: [], created: [], resolved: [] })
  }
  if (!silent) commit('SET_OVERVIEW_LOADING', { slot: 'chartMonthly', value: false })
  return r
}

// --- Server-side filter actions (Board + Overview) ---------------------
// Estas actions são commits puros: elas atualizam o estado do filtro mas
// NÃO disparam reload automaticamente. O reload é controlado pelo
// componente, via `apply*Filters`:
//   - Board: `applyBoardFilters` recarrega `loadTasks` SEM silent (o
//     skeleton full-screen só aparece no cold-load; o reload aparece no
//     `v-progress-linear` escopado do `task-list-view`).
//   - Overview: `applyOverviewFilters` recarrega os `loadOverview*` com
//     `silent: true` para não piscar os painéis (cada um tem seu próprio
//     slot de loading).
//
// Isso desacopla "estado do filtro" de "quando refetch acontece" — a UX é
// explícita (usuário clica Filtrar) e o spinner fica no próprio botão.

export function setBoardFilters({ commit }, partial) {
  commit('SET_BOARD_FILTERS', partial)
}

export function clearBoardFilters({ commit }) {
  commit('CLEAR_BOARD_FILTERS')
}

export function setOverviewFilters({ commit }, partial) {
  commit('SET_OVERVIEW_FILTERS', partial)
}

export function clearOverviewFilters({ commit }) {
  commit('CLEAR_OVERVIEW_FILTERS')
}

// Helpers de "apply": commit do filtro + reload. Usados pelos botões
// Filtrar/Limpar; retornam a promise do reload para que o componente
// possa await + desligar o loading do botão.

export function applyBoardFilters(
  { commit, dispatch, state },
  { dateFilter, boardFilters } = {}
) {
  if (dateFilter !== undefined) commit('SET_DATE_FILTER', dateFilter)
  if (boardFilters !== undefined) commit('SET_BOARD_FILTERS', boardFilters)
  // Reload SEM silent: state.loading flipa normalmente, mas o full-screen
  // skeleton da view-task-board está guardado por `loading && !hasFolders`
  // (cold-load only) — então a tela inteira não pisca. O indicador de
  // reload fica ESCOPADO no `task-list-view` (v-progress-linear no topo
  // quando `isLoading && hasAnyTask`).
  return dispatch('loadTasks', { sprintId: state.activeSprintId })
}

export function applyOverviewFilters({ commit, dispatch }, partial) {
  if (partial !== undefined) commit('SET_OVERVIEW_FILTERS', partial)
  // `silent: true` nos 4 painéis grandes mantém o conteúdo no lugar (cada
  // um tem um slot `state.overview.loading.X` que é flipped sem silent).
  // `loadOverviewStats` é assimétrico de propósito: não tem slot próprio
  // de loading nem flipa o global; o `task-summary-cards` consome o
  // `state.tarefas.loading` global apenas no cold-load. Adicionar `silent`
  // aqui seria no-op porque a action nem flipa loading.
  // forceUpdate: invalida TTL para sempre buscar com os novos filtros.
  return Promise.all([
    dispatch('loadOverviewStats'),
    dispatch('loadOverviewOverdueTasks', { forceUpdate: true, silent: true }),
    dispatch('loadOverviewMemberDistribution', { forceUpdate: true, silent: true }),
    dispatch('loadOverviewClientDistribution', { forceUpdate: true, silent: true }),
    dispatch('loadOverviewChartMonthly', { forceUpdate: true, silent: true }),
  ])
}

// Duplicate View / Espelhamento de tarefa — setters puros.
// Quem busca e persiste é `apiTaskView` (classe de API); a action só commita.
export function setTaskMirrors({ commit }, { taskId, mirrors }) {
  commit('SET_TASK_MIRRORS', { taskId, mirrors })
}

export function setTaskMirrorsLoading({ commit }, { taskId, loading }) {
  commit('SET_TASK_MIRRORS_LOADING', { taskId, loading })
}
